import {makeAutoObservable} from 'mobx';
import witAuditService, {WIT_AUDIT_PAGE_SIZE} from "../services/witAuditService";
import witTrainingQueueService from "../services/witTrainingQueueService";
import {createWitTrainingKey, hashWitText, normalizeWitText} from "../utils/witText";

const normalizeFilterValue = (value) => (value && value !== 'all' ? value : 'all');
const NOISY_DECISIONS = ['already_interacted', 'like_counter_too_low'];

export class WitAuditState {
  rootStore;
  entries = [];
  dataLoaded = false;
  loading = false;
  error = '';
  loadedEntryLimit = WIT_AUDIT_PAGE_SIZE;
  containsLegacyEntries = false;
  activeUid = null;
  requestGeneration = 0;
  inFlightRequests = new Map();
  decisionFilter = 'all';
  intentFilter = 'all';
  reviewFilter = 'unreviewed';
  searchText = '';
  showNoisyDecisions = false;
  savingReviewIds = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setEntries(entries) {
    this.entries = Array.isArray(entries) ? entries : [];
  }

  getCurrentUid() {
    return this.rootStore?.authState?.user?.uid || null;
  }

  reset() {
    this.requestGeneration += 1;
    this.entries = [];
    this.dataLoaded = false;
    this.loading = false;
    this.error = '';
    this.containsLegacyEntries = false;
    this.activeUid = null;
    this.inFlightRequests.clear();
  }

  prepareUser(uid) {
    if (this.activeUid !== uid) {
      this.reset();
      this.activeUid = uid;
    }
  }

  setDecisionFilter(value) {
    this.decisionFilter = normalizeFilterValue(value);
  }

  setIntentFilter(value) {
    this.intentFilter = normalizeFilterValue(value);
  }

  setReviewFilter(value) {
    this.reviewFilter = normalizeFilterValue(value);
  }

  setSearchText(value) {
    this.searchText = value || '';
  }

  setShowNoisyDecisions(value) {
    this.showNoisyDecisions = !!value;
  }

  resetInboxFilters() {
    this.decisionFilter = 'all';
    this.intentFilter = 'all';
    this.reviewFilter = 'unreviewed';
    this.searchText = '';
    this.showNoisyDecisions = false;
  }

  getDecisionOptions() {
    return [...new Set(this.entries.map((entry) => entry.decision).filter(Boolean))].sort();
  }

  getIntentOptions() {
    return [...new Set(this.entries.flatMap((entry) => [entry.responseIntent, entry.currentIntent, entry.reviewedIntent]).filter(Boolean))].sort();
  }

  hasReviewedLabel(entry) {
    return !!((entry?.reviewedIntent || '').trim() || (entry?.reviewedEntityType || '').trim());
  }

  getCounts() {
    return {
      total: this.entries.length,
      unreviewed: this.entries.filter((entry) => !this.hasReviewedLabel(entry) && entry.reviewStatus !== 'queued' && entry.reviewStatus !== 'skipped').length,
      reviewed: this.entries.filter((entry) => this.hasReviewedLabel(entry) && entry.reviewStatus !== 'queued').length,
      queued: this.entries.filter((entry) => entry.reviewStatus === 'queued').length,
      skipped: this.entries.filter((entry) => entry.reviewStatus === 'skipped').length,
      actionable: this.entries.filter((entry) => !NOISY_DECISIONS.includes(entry.decision)).length,
    };
  }

  getFilteredEntries() {
    const searchNeedle = this.searchText.trim().toLowerCase();
    return this.entries.filter((entry) => {
      const decision = entry.decision || 'pending';
      const reviewStatus = entry.reviewStatus || 'new';
      if (!this.showNoisyDecisions && NOISY_DECISIONS.includes(decision)) {
        return false;
      }
      if (this.decisionFilter !== 'all' && decision !== this.decisionFilter) {
        return false;
      }
      if (this.intentFilter !== 'all') {
        const intents = [entry.responseIntent, entry.currentIntent, entry.reviewedIntent].filter(Boolean);
        if (!intents.includes(this.intentFilter)) {
          return false;
        }
      }
      if (this.reviewFilter === 'reviewed' && (!this.hasReviewedLabel(entry) || reviewStatus === 'queued')) {
        return false;
      }
      if (this.reviewFilter === 'unreviewed' && (this.hasReviewedLabel(entry) || reviewStatus === 'queued' || reviewStatus === 'skipped')) {
        return false;
      }
      if (this.reviewFilter === 'queued' && reviewStatus !== 'queued') {
        return false;
      }
      if (this.reviewFilter === 'skipped' && reviewStatus !== 'skipped') {
        return false;
      }
      if (searchNeedle) {
        const haystack = [entry.text, entry.details, entry.responseIntent, entry.currentIntent, entry.reviewedIntent, entry.reviewedEntityType, entry.reviewedEntityValue, decision]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(searchNeedle)) {
          return false;
        }
      }
      return true;
    });
  }

  getVisibleQueueCandidates() {
    return this.getFilteredEntries().filter((entry) => this.hasReviewedLabel(entry) && entry.reviewStatus !== 'queued');
  }

  isSavingReview(entryId) {
    return this.savingReviewIds.includes(entryId);
  }

  loadAuditEntries({uid = this.getCurrentUid()} = {}) {
    this.prepareUser(uid);
    const existingRequest = this.inFlightRequests.get(uid);
    if (existingRequest) {
      return existingRequest;
    }

    const requestGeneration = this.requestGeneration;
    this.loading = true;
    this.error = '';

    let request;
    request = witAuditService.loadAuditEntries()
      .then(({entries, containsLegacyEntries}) => {
        if (this.requestGeneration !== requestGeneration || this.activeUid !== uid) {
          return;
        }
        this.setEntries(entries);
        this.containsLegacyEntries = containsLegacyEntries;
        this.dataLoaded = true;
      })
      .catch((reason) => {
        if (this.requestGeneration === requestGeneration && this.activeUid === uid) {
          console.error(reason);
          this.error = reason?.message || 'Failed to load Wit audit entries.';
        }
      })
      .finally(() => {
        if (this.inFlightRequests.get(uid) === request) {
          this.inFlightRequests.delete(uid);
        }
        if (this.requestGeneration === requestGeneration && this.activeUid === uid) {
          this.loading = false;
        }
      });
    this.inFlightRequests.set(uid, request);
    return request;
  }

  async enqueueReviewedEntry(entryId) {
    const entry = this.entries.find((item) => item.id === entryId);
    const reviewedIntent = (entry?.reviewedIntent || '').trim();
    const reviewedEntityType = (entry?.reviewedEntityType || '').trim();
    const reviewedEntityValue = (entry?.reviewedEntityValue || '').trim();
    const trainingKind = reviewedEntityType ? 'entity' : 'intent';
    const queueLabel = trainingKind === 'entity' ? reviewedEntityType : reviewedIntent;
    if (!entry || !queueLabel) {
      return;
    }

    const uid = this.rootStore?.authState?.user?.uid || null;
    const now = new Date().toISOString();
    const text = entry.text || '';
    const textHash = hashWitText(text);
    const queueEntryId = createWitTrainingKey(text, queueLabel, trainingKind, reviewedEntityValue);
    const normalizedText = normalizeWitText(text);
    const existingQueueEntry = this.rootStore?.witTrainingQueueState?.entries?.find?.((item) =>
      item?.id === queueEntryId
      || (
        item?.textHash === textHash
        && item?.trainingKind === trainingKind
        && (
          (trainingKind === 'entity' && item?.entityType === reviewedEntityType && item?.entityValue === reviewedEntityValue)
          || (trainingKind === 'intent' && item?.intent === reviewedIntent)
        )
      )
    );
    const duplicateSourceAuditIds = [...new Set([
      ...(Array.isArray(existingQueueEntry?.duplicateSourceAuditIds) ? existingQueueEntry.duplicateSourceAuditIds : []),
      existingQueueEntry?.sourceAuditId,
      entry.id,
    ].filter(Boolean))];
    const queueEntry = {
      id: queueEntryId,
      text,
      normalizedText,
      textHash,
      trainingKind,
      intent: trainingKind === 'intent' ? reviewedIntent : 'none',
      entityType: trainingKind === 'entity' ? reviewedEntityType : null,
      entityValue: trainingKind === 'entity' ? reviewedEntityValue : null,
      sourceAuditId: existingQueueEntry?.sourceAuditId || entry.id,
      duplicateSourceAuditIds,
      duplicateCount: duplicateSourceAuditIds.length,
      actionKey: entry.actionKey || null,
      currentIntent: entry.currentIntent || null,
      responseIntent: entry.responseIntent || null,
      responseConfidence: entry.responseConfidence ?? null,
      decision: entry.decision || null,
      status: existingQueueEntry?.status || 'pending',
      error: existingQueueEntry?.error || null,
      createdAt: existingQueueEntry?.createdAt || entry.queuedAt || now,
      updatedAt: now,
      queuedBy: uid,
      lastQueuedFromAuditId: entry.id,
    };

    this.savingReviewIds = [...this.savingReviewIds, entryId];

    try {
      await witTrainingQueueService.saveQueueEntry(queueEntryId, queueEntry);
      if (this.rootStore?.witTrainingQueueState?.saveQueueEntry) {
        await this.rootStore.witTrainingQueueState.saveQueueEntry(queueEntry);
      }
      await witAuditService.saveReview(entryId, {
        reviewStatus: 'queued',
        queuedAt: now,
        queuedBy: uid,
        queueEntryId,
        duplicateCount: duplicateSourceAuditIds.length,
        updatedAt: now,
      });
      this.entries = this.entries.map((item) => item.id === entryId ? {...item, reviewStatus: 'queued', queuedAt: now, queuedBy: uid, queueEntryId, duplicateCount: duplicateSourceAuditIds.length, updatedAt: now} : item);
    } catch (error) {
      console.error(error);
    } finally {
      this.savingReviewIds = this.savingReviewIds.filter((id) => id !== entryId);
    }
  }

  async enqueueVisibleReviewedEntries() {
    const candidates = this.getVisibleQueueCandidates();
    for (const entry of candidates) {
      // eslint-disable-next-line no-await-in-loop
      await this.enqueueReviewedEntry(entry.id);
    }
  }

  clearAuditEntries() {
    return witAuditService.clearAuditEntries()
      .then(() => {
        this.setEntries([]);
      })
      .catch(console.error);
  }

  saveReviewedLabels(entryId, {reviewedIntent, reviewedEntityType, reviewedEntityValue}) {
    const entry = this.entries.find((item) => item.id === entryId);
    if (!entryId || !entry) {
      return Promise.resolve();
    }

    const cleanReviewedIntent = (reviewedIntent || '').trim();
    const cleanReviewedEntityType = (reviewedEntityType || '').trim();
    const cleanReviewedEntityValue = (reviewedEntityValue || '').trim();
    const hasReview = !!(cleanReviewedIntent || cleanReviewedEntityType);
    const uid = this.rootStore?.authState?.user?.uid || null;
    const patch = {
      reviewedIntent: cleanReviewedIntent || null,
      reviewedEntityType: cleanReviewedEntityType || null,
      reviewedEntityValue: cleanReviewedEntityValue || null,
      reviewStatus: hasReview ? 'reviewed' : 'new',
      reviewedAt: new Date().toISOString(),
      reviewedBy: uid,
      updatedAt: new Date().toISOString(),
    };

    this.savingReviewIds = [...this.savingReviewIds, entryId];

    return witAuditService.saveReview(entryId, patch)
      .then(() => {
        this.entries = this.entries.map((item) => item.id === entryId ? {...item, ...patch} : item);
      })
      .catch(console.error)
      .finally(() => {
        this.savingReviewIds = this.savingReviewIds.filter((id) => id !== entryId);
      });
  }

  skipEntry(entryId) {
    const entry = this.entries.find((item) => item.id === entryId);
    if (!entryId || !entry) {
      return Promise.resolve();
    }

    const uid = this.rootStore?.authState?.user?.uid || null;
    const patch = {
      reviewedIntent: null,
      reviewedEntityType: null,
      reviewedEntityValue: null,
      reviewStatus: 'skipped',
      reviewedAt: new Date().toISOString(),
      reviewedBy: uid,
      updatedAt: new Date().toISOString(),
    };

    this.savingReviewIds = [...this.savingReviewIds, entryId];

    return witAuditService.saveReview(entryId, patch)
      .then(() => {
        this.entries = this.entries.map((item) => item.id === entryId ? {...item, ...patch} : item);
      })
      .catch(console.error)
      .finally(() => {
        this.savingReviewIds = this.savingReviewIds.filter((id) => id !== entryId);
      });
  }

  exportEntriesText(entries = this.getFilteredEntries()) {
    return JSON.stringify(entries, null, 2);
  }
}
